import React from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  Chip,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const Section = ({ title, children }: any) => (
  <Card sx={{ borderRadius: 3, boxShadow: 1 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const Privacy: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Privacy Policy
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label="Last updated: April 2026" size="small" />
          <Chip label="GDPR Ready" color="success" size="small" />
        </Stack>
      </Box>

      <Stack spacing={3}>

        {/* 1 */}
        <Section title="1. Overview">
          <Typography paragraph>
            This Privacy Policy explains how we collect, use, and protect your
            information when you use our AI-powered media processing Service.
          </Typography>
          <Typography>
            By using the Service, you agree to this Privacy Policy.
          </Typography>
        </Section>

        {/* 2 */}
        <Section title="2. Information We Collect">
          <Typography fontWeight={600} gutterBottom>
            Account Information
          </Typography>
          <ul>
            <li>Email address</li>
            <li>Authentication data (securely managed)</li>
          </ul>

          <Typography fontWeight={600} sx={{ mt: 2 }} gutterBottom>
            Uploaded Files
          </Typography>
          <Typography paragraph>
            Files you upload are processed temporarily to provide features such as
            transcription, compression, or conversion.
          </Typography>

            <Typography paragraph>
            Your uploaded files are processed securely and are not used to train AI models.
          </Typography>

          <Typography>
            These files are not permanently stored and may be automatically deleted
            after processing or download.
          </Typography>
        

          <Typography fontWeight={600} sx={{ mt: 2 }} gutterBottom>
            Usage Data (Limited Analytics)
          </Typography>
          <Typography paragraph>
            We collect minimal, non-sensitive usage data to improve the Service.
          </Typography>
          <ul>
            <li>paywall_viewed</li>
            <li>upgrade_clicked</li>
            <li>checkout_started</li>
            <li>purchase_completed</li>
          </ul>

          <Typography paragraph sx={{ mt: 2 }}>
            We do not collect or store sensitive personal data such as passwords,
            payment details, or authentication tokens.
          </Typography>

          <Typography fontWeight={600} sx={{ mt: 2 }} gutterBottom>
            Payment Information
          </Typography>
          <Typography>
            Payments are securely processed by third-party providers (e.g., Stripe).
            We do not store or have access to your payment details.
          </Typography>
        </Section>

        {/* 3 */}
        <Section title="3. How We Use Your Data">
          <ul>
            <li>Provide and operate the Service</li>
            <li>Process uploaded files and generate outputs</li>
            <li>Manage user accounts and subscriptions</li>
            <li>Improve performance and user experience</li>
          </ul>
        </Section>

        {/* 4 */}
        <Section title="4. AI Processing">
        <Typography paragraph>
          The Service uses artificial intelligence technologies, including third-party APIs,
          to process uploaded media and generate outputs such as transcripts and summaries.
        </Typography>

        <Typography paragraph>
          For transcription features, audio data may be securely sent to OpenAI (Whisper API)
          for processing.
        </Typography>

        <Typography paragraph>
          OpenAI processes data in accordance with its own privacy and security policies.
        </Typography>

        <Typography>
          We do not use your data to train AI models unless explicitly stated.
        </Typography>
      </Section>

        {/* 5 */}
        <Section title="5. Data Storage & Retention">
          <Typography paragraph>
            We retain personal data only as long as necessary to provide the Service.
          </Typography>
          <ul>
            <li>Uploaded files are temporary and may be deleted automatically</li>
            <li>Processed outputs may be removed after download or expiration</li>
            <li>Account data is retained while your account is active</li>
          </ul>
        </Section>

        {/* 6 */}
        <Section title="6. Your Rights (GDPR)">
          <Typography paragraph>
            If you are located in the European Economic Area (EEA), you have the following rights:
          </Typography>
          <ul>
            <li>Access your personal data</li>
            <li>Request correction or deletion</li>
            <li>Restrict or object to processing</li>
            <li>Request data portability</li>
          </ul>
          <Typography sx={{ mt: 1 }}>
            To exercise your rights, contact:
          <Link component={RouterLink} to="/contact" sx={{ mx: 1 }}>
                                    Support
            </Link>
            
          </Typography>
        </Section>

        {/* 7 */}
        <Section title="7. Third-Party Services">
          <Typography paragraph>
            We use trusted third-party service providers to operate and improve the Service.
          </Typography>

          <ul>
            <li>
              <strong>OpenAI (Whisper API):</strong> Used for audio transcription and AI processing.
              Uploaded audio data may be securely transmitted to OpenAI for processing in accordance
              with their privacy policy.
            </li>

            <li>
              <strong>Stripe:</strong> Used for payment processing. We do not store payment details.
            </li>
          </ul>

          <Typography sx={{ mt: 2 }}>
            These providers process data only as necessary to deliver their services and are
            obligated to protect your data under applicable laws.
          </Typography>
        </Section>

        {/* 8 */}
        <Section title="8. Cookies">
          <Typography>
            We use cookies and similar technologies to maintain sessions and improve
            user experience. You may control cookies through your browser settings.
          </Typography>
        </Section>

        {/* 9 */}
        <Section title="9. Data Security">
          <Typography>
            We implement reasonable technical and organizational safeguards to protect
            your data. However, no system is completely secure.
          </Typography>
        </Section>

        {/* 10 */}
        <Section title="10. Changes to This Policy">
          <Typography>
            We may update this Privacy Policy at any time. Continued use of the
            Service constitutes acceptance of the updated policy.
          </Typography>
        </Section>

        {/* 11 */}
        <Section title="11. Contact">
          <Typography>
            <Link component={RouterLink} to="/contact" sx={{ mx: 1 }}>
                      Contact Support
              </Link>
          </Typography>
        </Section>

      </Stack>
    </Container>
  );
};

export default Privacy;